import { useEffect, useState } from "react";
import type { DemoPersona } from "@/api/auth";

interface DemoSession {
  isDemo: boolean;
  persona: DemoPersona | null;
  expiresAt: number | null;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const mid = token.split(".")[1];
    if (!mid) return null;
    const padded = mid.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(padded + "===".slice((padded.length + 3) % 4));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Surface the demo state of the currently installed access token.
 *
 * The JWT is already trusted by the backend — we only peek at the payload
 * here to drive UI (banner copy, persona label, AI cap). Keys we read must
 * therefore never be used for authorization decisions on the client.
 */
export function useDemoSession(): DemoSession {
  const [state, setState] = useState<DemoSession>(() => read());

  useEffect(() => {
    const refresh = () => setState(read());
    // Storage events fire for OTHER tabs. Our own setAccessToken writes to
    // localStorage synchronously so we also watch the auth:logout signal and
    // a custom demo:session event dispatched after demoLogin.
    window.addEventListener("storage", refresh);
    window.addEventListener("auth:logout", refresh);
    window.addEventListener("demo:session", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("auth:logout", refresh);
      window.removeEventListener("demo:session", refresh);
    };
  }, []);

  return state;
}

function read(): DemoSession {
  const token = localStorage.getItem("access_token");
  if (!token) return { isDemo: false, persona: null, expiresAt: null };
  const payload = decodeJwtPayload(token);
  if (!payload || !payload["demo"]) {
    return { isDemo: false, persona: null, expiresAt: null };
  }
  const persona = (payload["demo_persona"] as DemoPersona | undefined) ?? null;
  const exp = typeof payload["exp"] === "number" ? (payload["exp"] as number) * 1000 : null;
  return { isDemo: true, persona, expiresAt: exp };
}
